import { useTranslation } from 'react-i18next';
import { Link as LinkIcon } from 'lucide-react';
import { Xmark } from 'iconoir-react';
import QRCode from 'react-qr-code';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';

const CHAT_LINK_PLACEHOLDER = 'https://t.me/your_bot';

type TelegramConnectedModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionUrl?: string | null;
  title?: string;
  logoSrc?: string;
  liveLabel?: string;
  fallbackUrl?: string;
};

export const TelegramConnectedModal = ({
  open,
  onOpenChange,
  connectionUrl,
  title,
  logoSrc = '/assets/images/agents/telegram-brand.svg',
  liveLabel,
  fallbackUrl = CHAT_LINK_PLACEHOLDER,
}: TelegramConnectedModalProps) => {
  const { t } = useTranslation();
  const chatLink = connectionUrl || fallbackUrl;

  const handleClose = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && handleClose()}>
      <DialogContent
        hideClose
        className="flex max-h-[90vh] w-full max-w-[565px] flex-col gap-6 overflow-y-auto rounded-xl border-0 bg-white p-4 shadow-[0_24px_80px_rgba(15,23,42,0.18)] dark:bg-background sm:p-8"
      >
        <button
          type="button"
          aria-label={t('CLOSE') || 'Close'}
          onClick={handleClose}
          className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center text-[#6D6D6D] focus:outline-none dark:text-true-white"
        >
          <Xmark width={14} height={14} />
        </button>

        <div className="flex items-center gap-3 pr-8">
          <img
            src={logoSrc}
            alt=""
            className="size-10 shrink-0 rounded-full object-cover"
          />
          <div className="flex min-w-0 flex-col gap-1">
            <DialogTitle className="font-sans text-2xl font-semibold not-italic leading-[120%] tracking-normal text-grey-700 dark:text-true-white">
              {title || t('TELEGRAM_CONNECTED', { defaultValue: 'Telegram Connected' })}
            </DialogTitle>
            <p className="font-sans text-sm font-normal not-italic leading-[160%] text-grey-500 dark:text-grey-400">
              {liveLabel || t('YOUR_BOT_IS_LIVE', { defaultValue: 'Your bot is live' })}
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-grey-100 bg-[#FAFAFA] p-5 dark:border-border dark:bg-muted/30 sm:p-6">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
            <div className="flex shrink-0 items-center justify-center rounded-xl border border-grey-100 bg-white p-3 dark:border-border dark:bg-background">
              <QRCode
                value={chatLink}
                size={148}
                style={{ height: 'auto', maxWidth: '100%', width: '148px' }}
              />
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-3">
              <div className="flex flex-col gap-2">
                <p className="font-sans text-base font-semibold not-italic leading-[120%] text-grey-700 dark:text-true-white">
                  {t('SCAN_TO_START_CHATTING', { defaultValue: 'Scan to start chatting' })}
                </p>
                <p className="font-sans text-sm font-normal not-italic leading-[160%] text-grey-500 dark:text-grey-400">
                  {t('TELEGRAM_SCAN_DESCRIPTION', {
                    defaultValue:
                      'Open the camera app on your phone and scan the code, or tap the link below to open the chat in your browser.',
                  })}
                </p>
              </div>

              <Button
                type="button"
                variant="outline"
                className="h-11 w-full justify-start gap-2 rounded-lg border border-grey-100 bg-white px-4 text-left font-sans text-sm font-normal text-[#2563eb] shadow-none hover:bg-white dark:border-border dark:bg-background"
                onClick={() => window.open(chatLink, '_blank', 'noopener,noreferrer')}
              >
                <LinkIcon className="size-4 shrink-0" aria-hidden />
                <span className="truncate">{chatLink}</span>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
